import { useState, useEffect, useRef, useCallback } from 'react';
import { Download, Trash2, Play, CheckCircle, Clock, AlertCircle, Calendar, ChevronDown, ChevronUp, Shield, Scale, Pause, PlayCircle, X, RotateCcw, CreditCard, Search } from 'lucide-react';
import { ProgressBar } from './ProgressBar';
import { ScheduleMessagingModal, ScheduleData } from './ScheduleMessagingModal';
import { RerunBatchModal, RerunData } from './RerunBatchModal';
import { AlertModal } from './AlertModal';
import { ExportCSVModal } from './ExportCSVModal';
import { Toast } from './Toast';
import { AddCardForm } from './AddCardForm';

// Date range filter options
type DateRangeOption = '14d' | '30d' | '90d' | 'year' | 'all' | 'custom';
const DATE_RANGE_OPTIONS: { value: DateRangeOption; label: string }[] = [
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'year', label: 'This year' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

// Next expansion mapping for progressive date range expansion
const NEXT_EXPANSION: Record<DateRangeOption, DateRangeOption | null> = {
  '14d': '30d',
  '30d': '90d',
  '90d': 'year',
  'year': 'all',
  'all': null,
  'custom': null, // No expansion for custom range
};

// Constants
const BATCH_PAGE_SIZE = 10;

interface MessagingStatus {
  queued: number;
  processing: number;
  paused: number;
  completed: number;
  failed: number;
  total: number;
  remaining: number;
  processStarted: boolean;
  intro_tag?: string | null; // Custom intro tag from contacts_to_tag
  batch: {
    tags_applied_count: number;
    messages_errored_count: number;
    messages_sent_at: string | null;
    messaging_scheduled_for: string | null;
  };
}

// Setup status for billing mode
interface SetupStatus {
  has_stripe_customer: boolean;
  has_payment_method: boolean;
  has_plan: boolean;
}

interface BatchListProps {
  batches: any[];
  onUpdate: () => void;
  onBatchUpdate: (batchId: string, updates: Partial<any>) => void;
  locationId?: string;      // For pagination API calls
  initialTotalCount?: number; // Total count from initial API response
  // All-time count (ignores date filter, respects search). Powers the empty
  // state's "no batches in this range" vs. "no batches ever" distinction.
  // null = API hasn't been deployed yet → fall back to legacy empty state.
  initialTotalCountAllTime?: number | null;
  // Billing mode props for wallet vs GHL
  billingMode?: 'ghl' | 'wallet' | null;
  walletSetupComplete?: boolean | null;
  setupStatus?: SetupStatus | null;
  currentBalance?: number | null; // Current wallet balance for auto top-up detection
  onRefreshBillingStatus?: (silent?: boolean) => void; // Called after payment to refresh billing state
  onShowAutoRechargeToast?: () => void; // Called when auto-recharge is triggered
  isInternal?: boolean; // Internal locations are exempt from the download gate
}

// Helper to calculate start date from date range option
function getStartDateFromRange(range: DateRangeOption): string | null {
  const now = new Date();
  switch (range) {
    case '14d':
      return new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString();
    case '30d':
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    case '90d':
      return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
    case 'year':
      return new Date(now.getFullYear(), 0, 1).toISOString();
    case 'all':
    case 'custom':
      return null; // Custom handled separately with explicit dates
  }
}

export function BatchList({
  batches,
  onUpdate,
  onBatchUpdate,
  locationId,
  initialTotalCount,
  initialTotalCountAllTime,
  billingMode,
  walletSetupComplete,
  setupStatus,
  currentBalance: _currentBalance, // Kept for potential future use
  onRefreshBillingStatus,
  onShowAutoRechargeToast,
  isInternal = false,
}: BatchListProps) {
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [schedulingBatchId, setSchedulingBatchId] = useState<string | null>(null);
  const [rerunningBatchId, setRerunningBatchId] = useState<string | null>(null);
  const [rescheduleMessagingBatchId, setRescheduleMessagingBatchId] = useState<string | null>(null);
  const [exportingBatchId, setExportingBatchId] = useState<string | null>(null);
  const [processingBatchIds, setProcessingBatchIds] = useState<Set<string>>(new Set());
  const [messagingStatuses, setMessagingStatuses] = useState<Map<string, MessagingStatus>>(new Map());
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [alertModal, setAlertModal] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; title: string; message: string; onConfirm: () => void; confirmLabel: string; confirmColor: string }>({ isOpen: false, title: '', message: '', onConfirm: () => {}, confirmLabel: 'OK', confirmColor: 'red' });

  // Billing setup modal for wallet mode
  const [showAddCardModal, setShowAddCardModal] = useState(false);

  // Determine if billing setup is incomplete (wallet mode + has plan but no payment method)
  const isSetupIncomplete = billingMode === 'wallet' && walletSetupComplete === false;

  // Pagination state - restore from localStorage if available
  const [dateRange, setDateRange] = useState<DateRangeOption>(() => {
    const saved = localStorage.getItem('batchList_dateRange');
    return (saved as DateRangeOption) || '14d';
  });
  const [customStartDate, setCustomStartDate] = useState<string>(() => {
    return localStorage.getItem('batchList_customStartDate') || '';
  });
  const [customEndDate, setCustomEndDate] = useState<string>(() => {
    return localStorage.getItem('batchList_customEndDate') || '';
  });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(() => {
    return localStorage.getItem('batchList_dateRange') === 'custom';
  });
  // Search state
  const [searchInput, setSearchInput] = useState(''); // Live input value
  const [activeSearch, setActiveSearch] = useState(''); // Committed search term (after Enter)
  const [savedDateRangeBeforeSearch, setSavedDateRangeBeforeSearch] = useState<DateRangeOption | null>(null); // Restore on clear

  const [displayedBatches, setDisplayedBatches] = useState<any[]>(batches);
  const [totalCount, setTotalCount] = useState<number>(initialTotalCount ?? batches.length);
  const [totalCountAllTime, setTotalCountAllTime] = useState<number | null>(initialTotalCountAllTime ?? null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoadedMore, setHasLoadedMore] = useState(false); // Track if user loaded beyond initial page
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info'; actions?: { label: string; onClick: () => void }[] } | null>(null);
  const previousBatchIdsRef = useRef<Set<string>>(new Set(batches.map(b => b.id)));

  // Derive locationId from batches if not provided
  const effectiveLocationId = locationId || batches[0]?.ghl_location_id;

  // Save date range settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('batchList_dateRange', dateRange);
  }, [dateRange]);

  useEffect(() => {
    localStorage.setItem('batchList_customStartDate', customStartDate);
  }, [customStartDate]);

  useEffect(() => {
    localStorage.setItem('batchList_customEndDate', customEndDate);
  }, [customEndDate]);

  // Track if initial load with saved settings has been done
  const initialLoadDoneRef = useRef(false);

  // Sync displayed batches when prop batches change (from parent refresh)
  // BUT: If user has loaded more batches, don't reset - just update existing batches in place
  useEffect(() => {
    if (hasLoadedMore) {
      // User loaded more - update existing batches in place, don't reset the list
      setDisplayedBatches(prev => {
        return prev.map(displayedBatch => {
          const updatedBatch = batches.find(b => b.id === displayedBatch.id);
          return updatedBatch || displayedBatch;
        });
      });
    } else {
      // Initial load or date range change - sync from props
      setDisplayedBatches(batches);
      if (initialTotalCount !== undefined) {
        setTotalCount(initialTotalCount);
      }
      if (initialTotalCountAllTime !== undefined) {
        setTotalCountAllTime(initialTotalCountAllTime);
      }
    }
  }, [batches, initialTotalCount, initialTotalCountAllTime, hasLoadedMore]);

  // Detect new batches created while browsing older batches
  useEffect(() => {
    const currentBatchIds = new Set(batches.map(b => b.id));
    const previousIds = previousBatchIdsRef.current;

    // Find new batch IDs that weren't in the previous set
    const newBatchIds = [...currentBatchIds].filter(id => !previousIds.has(id));

    if (newBatchIds.length > 0 && previousIds.size > 0) {
      // A new batch was created - show toast notification
      const newBatch = batches.find(b => b.id === newBatchIds[0]);
      if (newBatch) {
        setToast({
          message: `New batch "${newBatch.batch_name}" was created`,
          type: 'info',
          actions: [
            {
              label: 'View Now',
              onClick: () => {
                // Scroll to top where new batch would be
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            },
            {
              label: 'Stay Here',
              onClick: () => {
                // Just dismiss toast
              }
            }
          ]
        });
      }
    }

    previousBatchIdsRef.current = currentBatchIds;
  }, [batches]);

  const showAlert = (title: string, message: string) => {
    setAlertModal({ isOpen: true, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, title: '', message: '' });
  };

  // Helper function to format duration between two timestamps
  const formatDuration = (startTime: string | null, endTime: string | null, useCurrentTime: boolean = false): string => {
    if (!startTime) return 'X';
    
    // If endTime is missing but useCurrentTime is true, use current time for real-time calculation
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : (useCurrentTime ? new Date() : null);
    
    if (!end) return 'X';
    
    const durationMs = end.getTime() - start.getTime();
    
    if (durationMs < 0) return 'X';
    
    const seconds = durationMs / 1000;
    const minutes = seconds / 60;
    const hours = minutes / 60;
    
    if (seconds < 60) {
      return `${seconds.toFixed(0)} sec.`;
    } else if (minutes < 60) {
      return `${minutes.toFixed(1)} min.`;
    } else {
      return `${hours.toFixed(1)} hours`;
    }
  };
  
  // Helper function specifically for messaging duration
  // Uses messaging_started_at (when CRON actually started enqueueing) for accurate duration
  const formatMessagingDuration = (batch: any): string => {
    if (!batch.messages_sent_at) return 'X';
    
    // Use messaging_started_at if available (when CRON actually started enqueueing contacts)
    // This is set by the CRON job when it first starts processing contacts for this batch
    const startTime = batch.messaging_started_at;
    if (!startTime) return 'X';
    
    return formatDuration(startTime, batch.messages_sent_at);
  };

  // Extract messaging status from batch data on initial load and when displayed batches change
  // IMPORTANT: Preserve higher counts to prevent "rubber banding" during updates
  // NOTE: Uses displayedBatches (not batches prop) so "Load More" and date range batches
  // beyond the initial page also get their messaging status initialized.
  useEffect(() => {
    displayedBatches.forEach(batch => {
      // Check if batch has messaging status fields from RPC function
      if (typeof batch.messaging_total === 'number') {
        setMessagingStatuses(prev => {
          const existing = prev.get(batch.id);

          // Use MAX of existing polling value vs batch data to prevent rubber-banding
          // This ensures counts only go up, never down (unless messaging is cancelled)
          const newCompleted = Math.max(
            existing?.completed || 0,
            batch.messaging_completed || 0,
            batch.tags_applied_count || 0
          );
          const newFailed = Math.max(
            existing?.failed || 0,
            batch.messaging_failed || 0,
            batch.messages_errored_count || 0
          );

          // Once polling has provided data (existing entry), preserve volatile fields
          // (queued/processing/paused) from polling — the tagging-progress endpoint is
          // the authoritative source. RPC batch data can lag behind, causing UI flicker
          // when both sources alternate writing different values.
          const messagingStatus: MessagingStatus = {
            queued: existing ? existing.queued : (batch.messaging_queued || 0),
            processing: existing ? existing.processing : (batch.messaging_processing || 0),
            paused: existing ? existing.paused : (batch.messaging_paused || 0),
            completed: newCompleted,
            failed: newFailed,
            total: batch.messaging_total || 0,
            remaining: Math.max(0, (batch.messaging_total || 0) - newCompleted - newFailed),
            processStarted: (batch.messaging_total || 0) > 0,
            intro_tag: existing?.intro_tag || null,
            batch: {
              tags_applied_count: Math.max(batch.tags_applied_count || 0, newCompleted),
              messages_errored_count: Math.max(batch.messages_errored_count || 0, newFailed),
              messages_sent_at: batch.messages_sent_at,
              messaging_scheduled_for: batch.messaging_scheduled_for
            }
          };

          return new Map(prev).set(batch.id, messagingStatus);
        });
      }
    });
  }, [displayedBatches]);

  // Granular polling for all three progress stages
  // NOTE: Uses displayedBatches (not batches prop) so "Load More" and date range batches
  // beyond the initial page also get polled for status updates.
  useEffect(() => {
    // Always poll if there are any batches that might need updates
    // Include recently completed batches to ensure final counts are fetched
    const hasActiveBatches = displayedBatches.some(b => {
      // Active states that need polling
      if (b.status === 'pending' || b.status === 'processing') {
        return true;
      }
      // Recently uploaded (within last 30 seconds) - poll to get final CSV validation counts
      if (b.status === 'upload_complete' && b.upload_completed_at) {
        const uploadedAt = new Date(b.upload_completed_at);
        const now = new Date();
        const secondsSinceUpload = (now.getTime() - uploadedAt.getTime()) / 1000;
        if (secondsSinceUpload < 30) {
          return true;
        }
      }
      // Messaging in progress
      if (b.ready_for_messaging && !b.messages_sent_at) {
        return true;
      }
      // Recently finished messaging (within last 10 seconds) - poll to get final tagging counts
      if (b.messages_sent_at) {
        const sentAt = new Date(b.messages_sent_at);
        const now = new Date();
        const secondsSinceSent = (now.getTime() - sentAt.getTime()) / 1000;
        if (secondsSinceSent < 10) {
          return true;
        }
      }
      // Recently completed processing (within last 30 seconds) - poll to get final processing counts
      if (b.status === 'completed' && b.processing_completed_at) {
        const completedAt = new Date(b.processing_completed_at);
        const now = new Date();
        const secondsSinceCompletion = (now.getTime() - completedAt.getTime()) / 1000;
        return secondsSinceCompletion < 30;
      }
      return false;
    });

    // Clear existing interval if it exists
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (hasActiveBatches) {
      // Poll every 2 seconds for granular updates
      pollingIntervalRef.current = setInterval(async () => {

        // Update all batches to catch state transitions
        for (const batch of displayedBatches) {
          // Stage 1: Upload/CSV validation (pending -> upload_complete)
          if (batch.status === 'pending') {
            await fetchBatchUpdate(batch.id);
          }
          // Stage 1.5: Recently uploaded - fetch final CSV validation counts
          else if (batch.status === 'upload_complete' && batch.upload_completed_at) {
            const uploadedAt = new Date(batch.upload_completed_at);
            const now = new Date();
            const secondsSinceUpload = (now.getTime() - uploadedAt.getTime()) / 1000;
            if (secondsSinceUpload < 30) {
              await fetchBatchUpdate(batch.id);
            }
          }
          // Stage 2: Contact processing (processing -> completed)
          else if (batch.status === 'processing') {
            await fetchBatchUpdate(batch.id);
          }
          // Stage 2.5: Recently completed - fetch final processing counts
          else if (batch.status === 'completed' && batch.processing_completed_at) {
            const completedAt = new Date(batch.processing_completed_at);
            const now = new Date();
            const secondsSinceCompletion = (now.getTime() - completedAt.getTime()) / 1000;
            if (secondsSinceCompletion < 30) {
              await fetchBatchUpdate(batch.id);
            }
          }
          // Stage 3: Message sending (ready_for_messaging -> messages_sent)
          if (batch.ready_for_messaging && !batch.messages_sent_at) {
            await fetchMessagingStatus(batch.id);
          }
          // Stage 3.5: Recently finished messaging - fetch final tagging counts
          else if (batch.messages_sent_at) {
            const sentAt = new Date(batch.messages_sent_at);
            const now = new Date();
            const secondsSinceSent = (now.getTime() - sentAt.getTime()) / 1000;
            if (secondsSinceSent < 10) {
              await fetchMessagingStatus(batch.id);
            }
          }
        }
      }, 2000);
    }

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [displayedBatches]);

  // Fetch granular batch update without full refresh
  const fetchBatchUpdate = async (batchId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/batches?id=${batchId}&_t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      if (response.ok) {
        const data = await response.json() as { batches?: any[] };
        const batch = data.batches?.[0];
        if (batch) {
          // Always update batch in parent state granularly (including state transitions)
          onBatchUpdate(batchId, batch);
        }
      } else {
        console.error(`[BatchList] Failed to fetch batch ${batchId}: ${response.status} ${response.statusText}`);
        const errorText = await response.text();
        console.error(`[BatchList] Error response:`, errorText);
      }
    } catch (error) {
      console.error('[BatchList] Error fetching batch update:', error);
      console.error('[BatchList] Batch ID:', batchId);
      console.error('[BatchList] API URL:', import.meta.env.VITE_API_URL);
    }
  };

  // Fetch messaging status for a batch using new tagging-progress endpoint
  const fetchMessagingStatus = async (batchId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/batches/${batchId}/tagging-progress?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json() as MessagingStatus;

        // Preserve higher counts to prevent rubber-banding
        setMessagingStatuses(prev => {
          const existing = prev.get(batchId);
          const newCompleted = Math.max(existing?.completed || 0, data.completed || 0, data.batch?.tags_applied_count || 0);
          const newFailed = Math.max(existing?.failed || 0, data.failed || 0, data.batch?.messages_errored_count || 0);

          return new Map(prev).set(batchId, {
            ...data,
            completed: newCompleted,
            failed: newFailed,
            remaining: Math.max(0, (data.total || 0) - newCompleted - newFailed),
            batch: {
              ...data.batch,
              tags_applied_count: Math.max(data.batch?.tags_applied_count || 0, newCompleted),
              messages_errored_count: Math.max(data.batch?.messages_errored_count || 0, newFailed)
            }
          });
        });

        // If all messages are completed or failed, update batch state granularly
        const allMessagesProcessed = data.total > 0 && (data.completed + data.failed) >= data.total;
        if (allMessagesProcessed) {
          
          // Fetch updated batch to get new state (messages_sent flag, etc.)
          const batchResponse = await fetch(`${import.meta.env.VITE_API_URL}/batches?id=${batchId}&_t=${Date.now()}`, {
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          });
          if (batchResponse.ok) {
            const batchData = await batchResponse.json() as { batches?: any[] };
            const batch = batchData.batches?.[0];
            if (batch) {
              onBatchUpdate(batchId, batch);
            }
          }
        }
      }
    } catch (error) {
      console.error('[BatchList] Error fetching messaging status:', error);
      console.error('[BatchList] Batch ID:', batchId);
    }
  };

  const toggleExpanded = (batchId: string) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  const handleDelete = (batchId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Batch',
      message: 'Are you sure you want to delete this batch?',
      confirmLabel: 'Delete',
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        // Optimistically remove from UI immediately so buttons disappear
        setDisplayedBatches(prev => prev.filter(b => b.id !== batchId));
        try {
          await fetch(`${import.meta.env.VITE_API_URL}/batches/${batchId}`, {
            method: 'DELETE',
          });
          onUpdate();
        } catch (error) {
          console.error('Delete failed:', error);
          // Restore on failure by re-fetching
          onUpdate();
        }
      },
    });
  };

  const handleReprocess = async (batchId: string) => {
    setRerunningBatchId(batchId);
  };

  const handleRerun = async (batchId: string, data: RerunData) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/batches/${batchId}/rerun`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || 'Failed to rerun batch');
      }

      await response.json() as { batchId: string; contactCount: number };
      
      onUpdate();
    } catch (error) {
      console.error('Rerun failed:', error);
      throw error;
    }
  };

  const handlePauseMessaging = async (batchId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/batches/${batchId}/pause-messaging`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        throw new Error('Failed to pause messaging');
      }
      
      // Immediately update messaging status
      await fetchMessagingStatus(batchId);
    } catch (error) {
      console.error('Pause messaging failed:', error);
      showAlert('Error', 'Failed to pause messaging: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleResumeMessaging = async (batchId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/batches/${batchId}/resume-messaging`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        throw new Error('Failed to resume messaging');
      }
      
      // Immediately update messaging status
      await fetchMessagingStatus(batchId);
    } catch (error) {
      console.error('Resume messaging failed:', error);
      showAlert('Error', 'Failed to resume messaging: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleRescheduleMessaging = async (batchId: string, data: ScheduleData) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/batches/${batchId}/reschedule-messaging`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json() as { error?: string; message?: string };
        throw new Error(errorData.message || errorData.error || 'Failed to reschedule messaging');
      }
      
      // Immediately update messaging status and batch
      await fetchMessagingStatus(batchId);
      await fetchBatchUpdate(batchId);
      setRescheduleMessagingBatchId(null);
    } catch (error) {
      console.error('Reschedule messaging failed:', error);
      showAlert('Error', 'Failed to reschedule messaging: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleCancelMessaging = (batchId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Cancel Messaging',
      message: 'Are you sure you want to cancel messaging for this batch? This action cannot be undone.',
      confirmLabel: 'Cancel Messaging',
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/batches/${batchId}/cancel-messaging`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });

          if (!response.ok) {
            const errorData = await response.json() as { error?: string; message?: string };
            throw new Error(errorData.message || errorData.error || 'Failed to cancel messaging');
          }

          // Immediately update messaging status and batch
          await fetchMessagingStatus(batchId);
          await fetchBatchUpdate(batchId);
          onUpdate();
        } catch (error) {
          console.error('Cancel messaging failed:', error);
          showAlert('Error', 'Failed to cancel messaging: ' + (error instanceof Error ? error.message : String(error)));
        }
      },
    });
  };

  const handleContinueProcessing = async (batchId: string) => {
    if (processingBatchIds.has(batchId)) return;
    setProcessingBatchIds(prev => {
      const next = new Set(prev);
      next.add(batchId);
      return next;
    });
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/batches/${batchId}/continue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json() as {
          success: boolean;
          autoRechargeTriggered?: boolean;
          autoRechargeAmount?: number;
        };
        onUpdate();

        // Show toast if auto-recharge was triggered
        if (result.autoRechargeTriggered) {
          onShowAutoRechargeToast?.();
        }

        // Refresh billing balance in header after successful payment (silent to avoid button flash)
        // Small delay to ensure database transaction is fully committed
        setTimeout(() => {
          onRefreshBillingStatus?.(true);
        }, 500);
      } else {
        const errorData = await response.json() as { error?: string; message?: string; walletBalance?: number; requiredAmount?: number; details?: string };
        console.error('[BatchList] Continue processing failed:', errorData);

        if (errorData.error === 'batch_already_started') {
          onUpdate();
        } else if (errorData.error === 'insufficient_funds') {
          const requiredAmount = errorData.requiredAmount ?? 0;
          showAlert('Insufficient Wallet Balance', `Required: $${requiredAmount.toFixed(2)}\n\nPlease add credits to your wallet and try again.`);
        } else {
          showAlert('Processing Failed', 'Failed to start processing: ' + (errorData.message || errorData.error || 'Unknown error') + (errorData.details ? `\n\nDetails: ${errorData.details}` : ''));
        }
      }
    } catch (error) {
      console.error('[BatchList] Continue processing exception:', error);
      showAlert('Error', 'Failed to start processing: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setProcessingBatchIds(prev => {
        const next = new Set(prev);
        next.delete(batchId);
        return next;
      });
    }
  };

  const handleScheduleMessaging = async (batchId: string, data: ScheduleData) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/batches/${batchId}/schedule-messaging`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const result = await response.json() as { contactCount: number };
        showAlert('Success', `Successfully scheduled ${result.contactCount} contacts for messaging with tag "z.intro"`);
        onUpdate();
      } else {
        const errorData = await response.json() as { error?: string; message?: string };
        console.error('[BatchList] Scheduling failed:', errorData);
        showAlert('Error', 'Failed to schedule messaging: ' + (errorData.message || errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('[BatchList] Scheduling exception:', error);
      throw error;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string, batch: any, messagingStatus?: MessagingStatus) => {
    // If batch is completed and has messaging (check multiple indicators for reliability)
    // Use batch.messages_sent_at or batch.tags_applied_count as fallback when contacts_to_tag is empty
    const hasMessagingData = (messagingStatus && messagingStatus.total > 0) ||
      batch.messages_sent_at ||
      messagingStatus?.batch?.messages_sent_at ||
      (batch.tags_applied_count && batch.tags_applied_count > 0);

    if (status === 'completed' && hasMessagingData) {
      const now = new Date();

      // Prefer messagingStatus.batch values (fresh from polling) over batch prop (potentially stale)
      const rawScheduledFor = messagingStatus?.batch?.messaging_scheduled_for ?? batch.messaging_scheduled_for;
      const rawSentAt = messagingStatus?.batch?.messages_sent_at ?? batch.messages_sent_at;

      // Helper to parse timestamps - handles various formats from Supabase:
      // - "2026-02-06T14:25:00Z" (ISO with Z)
      // - "2026-02-06 14:25:00+00" (Postgres with offset)
      // - "2026-02-06T14:25:00.000Z" (ISO with milliseconds)
      const parseTimestamp = (ts: string | null | undefined): Date | null => {
        if (!ts) return null;

        // Normalize Postgres format to ISO 8601:
        // "2026-02-06 14:25:00+00" -> "2026-02-06T14:25:00+00:00"
        let normalized = ts.replace(' ', 'T');

        // Fix short timezone offset: +00 -> +00:00
        if (/[+-]\d{2}$/.test(normalized)) {
          normalized = normalized + ':00';
        }

        // If no timezone info, assume UTC
        if (!normalized.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(normalized)) {
          normalized = normalized + 'Z';
        }

        return new Date(normalized);
      };

      const scheduledFor = parseTimestamp(rawScheduledFor);
      const sentAt = parseTimestamp(rawSentAt);

      // Messages have been sent - show when they started
      if (sentAt) {
        // Use messaging_started_at (when CRON actually started enqueueing) as the actual start time
        const rawStartedAt = batch.messaging_started_at ?? rawScheduledFor;
        const startedAt = parseTimestamp(rawStartedAt);
        if (startedAt) {
          const dateStr = startedAt.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: 'numeric' });
          const timeStr = startedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
          return `Messages Sent Starting ${dateStr} ${timeStr}`;
        }
        return 'Messages Sent';
      }

      // Messaging is in progress (after scheduled time)
      if (scheduledFor && now >= scheduledFor) {
        return 'Sending Messages...';
      }

      // Scheduled for future
      if (scheduledFor && now < scheduledFor) {
        return `Scheduled For ${scheduledFor.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: 'numeric' })} ${scheduledFor.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })}`;
      }
    }

    switch (status) {
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'Processing';
      case 'failed':
        return 'Failed';
      case 'pending':
        return 'Pending';
      case 'upload_complete':
        return 'Upload Complete';
      default:
        return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
  };

  // Bare empty state — only when there are truly zero batches for this location
  // (no filter, no search ever returns anything). Show the original message with
  // no surrounding chrome so a brand-new user has a clean "get started" view.
  // Legacy behavior: when totalCountAllTime is null (API not deployed yet), fall
  // back to the original batches.length === 0 check.
  const noBatchesEver = totalCountAllTime === null
    ? batches.length === 0
    : totalCountAllTime === 0;
  if (noBatchesEver) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <p className="text-gray-500">No batches yet. Upload a CSV file to get started.</p>
      </div>
    );
  }

  // Manual refresh that preserves current date range and loaded count
  const handleManualRefresh = async () => {
    setIsLoadingMore(true);

    try {
      // Preserve at least the current displayed count
      const limit = Math.max(displayedBatches.length, BATCH_PAGE_SIZE);
      const params = new URLSearchParams({
        locationId: effectiveLocationId,
        limit: limit.toString(),
        offset: '0',
      });

      // When search is active, pass search text instead of date range
      if (activeSearch) {
        params.append('searchText', activeSearch);
      } else {
        // Apply current date range filter
        if (dateRange === 'custom' && customStartDate) {
          params.append('startDate', new Date(customStartDate + 'T00:00:00').toISOString());
          if (customEndDate) {
            params.append('endDate', new Date(customEndDate + 'T23:59:59.999').toISOString());
          }
        } else if (dateRange !== 'all') {
          const startDate = getStartDateFromRange(dateRange);
          if (startDate) {
            params.append('startDate', startDate);
          }
        }
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/batches?${params.toString()}&_t=${Date.now()}`,
        {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );

      if (response.ok) {
        const data = await response.json() as { batches: any[]; totalCount?: number; totalCountAllTime?: number | null };
        setDisplayedBatches(data.batches || []);
        setTotalCount(data.totalCount ?? data.batches?.length ?? 0);
        if (data.totalCountAllTime !== undefined) {
          setTotalCountAllTime(data.totalCountAllTime);
        }
        // Protect from parent overwrite if we have more than default or search is active
        if ((data.batches?.length || 0) > BATCH_PAGE_SIZE || activeSearch) {
          setHasLoadedMore(true);
        }
      }
    } catch (error) {
      console.error('[BatchList] Error refreshing batches:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle date range filter change
  const handleDateRangeChange = useCallback(async (newRange: DateRangeOption) => {
    setDateRange(newRange);
    setHasLoadedMore(false); // Reset since we're starting fresh with new date range

    // If custom is selected, show the date picker instead of fetching
    if (newRange === 'custom') {
      setShowCustomDatePicker(true);
      return;
    }

    setShowCustomDatePicker(false);
    setIsLoadingMore(true);

    try {
      const startDate = getStartDateFromRange(newRange);
      // When expanding date range, load current count + one more page
      // This ensures user sees more batches when they click "Expand"
      const limit = displayedBatches.length + BATCH_PAGE_SIZE;
      const params = new URLSearchParams({
        locationId: effectiveLocationId,
        limit: limit.toString(),
        offset: '0',
      });
      if (startDate) {
        params.append('startDate', startDate);
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/batches?${params.toString()}&_t=${Date.now()}`,
        {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );

      if (response.ok) {
        const data = await response.json() as { batches: any[]; totalCount?: number; totalCountAllTime?: number | null };
        setDisplayedBatches(data.batches || []);
        setTotalCount(data.totalCount ?? data.batches?.length ?? 0);
        if (data.totalCountAllTime !== undefined) {
          setTotalCountAllTime(data.totalCountAllTime);
        }
        // Protect these batches from being overwritten by parent refresh
        // (since we loaded more than the parent's default 10)
        if ((data.batches?.length || 0) > BATCH_PAGE_SIZE) {
          setHasLoadedMore(true);
        }
      }
    } catch (error) {
      console.error('[BatchList] Error changing date range:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [effectiveLocationId, displayedBatches.length]);

  // Handle custom date range search
  const handleCustomDateSearch = useCallback(async () => {
    if (!customStartDate) {
      return; // At minimum need a start date
    }

    setIsLoadingMore(true);

    try {
      const params = new URLSearchParams({
        locationId: effectiveLocationId,
        limit: BATCH_PAGE_SIZE.toString(),
        offset: '0',
      });

      // Parse date as LOCAL time (add T00:00:00 to avoid UTC parsing)
      // new Date('2026-01-05') parses as UTC midnight, but
      // new Date('2026-01-05T00:00:00') parses as local midnight
      params.append('startDate', new Date(customStartDate + 'T00:00:00').toISOString());

      if (customEndDate) {
        // End date should be end of that day in LOCAL time
        params.append('endDate', new Date(customEndDate + 'T23:59:59.999').toISOString());
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/batches?${params.toString()}&_t=${Date.now()}`,
        {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );

      if (response.ok) {
        const data = await response.json() as { batches: any[]; totalCount?: number; totalCountAllTime?: number | null };
        setDisplayedBatches(data.batches || []);
        setTotalCount(data.totalCount ?? data.batches?.length ?? 0);
        if (data.totalCountAllTime !== undefined) {
          setTotalCountAllTime(data.totalCountAllTime);
        }
        // Always protect custom date range results from parent refresh
        // (parent uses default 14-day filter which would overwrite our custom filter)
        setHasLoadedMore(true);
      }
    } catch (error) {
      console.error('[BatchList] Error fetching custom date range:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [effectiveLocationId, customStartDate, customEndDate]);

  // Load with saved date range on mount (if different from parent's default 14-day)
  useEffect(() => {
    if (initialLoadDoneRef.current || !effectiveLocationId) return;
    initialLoadDoneRef.current = true;

    const savedDateRange = localStorage.getItem('batchList_dateRange') as DateRangeOption;

    // If saved date range is different from 14d (parent's default), load with saved settings
    if (savedDateRange && savedDateRange !== '14d') {
      if (savedDateRange === 'custom') {
        const savedStart = localStorage.getItem('batchList_customStartDate');
        if (savedStart) {
          // Trigger custom date search with saved values
          handleCustomDateSearch();
        }
      } else {
        // Trigger date range change with saved value
        handleDateRangeChange(savedDateRange);
      }
    }
  }, [effectiveLocationId, handleDateRangeChange, handleCustomDateSearch]);

  // Handle Load More button click
  const handleLoadMore = useCallback(async () => {
    setIsLoadingMore(true);

    try {
      const params = new URLSearchParams({
        locationId: effectiveLocationId,
        limit: BATCH_PAGE_SIZE.toString(),
        offset: displayedBatches.length.toString(),
      });

      // When search is active, pass search text instead of date range
      if (activeSearch) {
        params.append('searchText', activeSearch);
      } else if (dateRange === 'custom' && customStartDate) {
        params.append('startDate', new Date(customStartDate + 'T00:00:00').toISOString());
        if (customEndDate) {
          params.append('endDate', new Date(customEndDate + 'T23:59:59.999').toISOString());
        }
      } else {
        const startDate = getStartDateFromRange(dateRange);
        if (startDate) {
          params.append('startDate', startDate);
        }
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/batches?${params.toString()}&_t=${Date.now()}`,
        {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );

      if (response.ok) {
        const data = await response.json() as { batches: any[]; totalCount?: number; totalCountAllTime?: number | null };
        // Append new batches to existing ones (older batches load below)
        setDisplayedBatches(prev => [...prev, ...(data.batches || [])]);
        setHasLoadedMore(true); // Mark that user has loaded beyond initial page
        if (data.totalCount !== undefined) {
          setTotalCount(data.totalCount);
        }
        if (data.totalCountAllTime !== undefined) {
          setTotalCountAllTime(data.totalCountAllTime);
        }
      }
    } catch (error) {
      console.error('[BatchList] Error loading more batches:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [effectiveLocationId, dateRange, displayedBatches.length, customStartDate, customEndDate, activeSearch]);

  // Handle progressive expansion (when all batches in current range are loaded)
  const handleExpandDateRange = useCallback(() => {
    const nextRange = NEXT_EXPANSION[dateRange];
    if (nextRange) {
      handleDateRangeChange(nextRange);
    }
  }, [dateRange, handleDateRangeChange]);

  // Handle search execution (Enter key)
  const handleSearchExecute = useCallback(async () => {
    const trimmed = searchInput.trim();
    if (trimmed.length < 2) return; // Minimum 2 characters

    setActiveSearch(trimmed);
    setHasLoadedMore(false);
    setIsLoadingMore(true);

    // Save current date range so we can restore it when search is cleared
    if (!savedDateRangeBeforeSearch) {
      setSavedDateRangeBeforeSearch(dateRange);
    }

    try {
      // Search across all time — no date filter
      const params = new URLSearchParams({
        locationId: effectiveLocationId,
        limit: BATCH_PAGE_SIZE.toString(),
        offset: '0',
        searchText: trimmed,
      });

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/batches?${params.toString()}&_t=${Date.now()}`,
        {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );

      if (response.ok) {
        const data = await response.json() as { batches: any[]; totalCount?: number; totalCountAllTime?: number | null };
        setDisplayedBatches(data.batches || []);
        setTotalCount(data.totalCount ?? data.batches?.length ?? 0);
        if (data.totalCountAllTime !== undefined) {
          setTotalCountAllTime(data.totalCountAllTime);
        }
        setHasLoadedMore(true); // Protect from parent overwrite
      }
    } catch (error) {
      console.error('[BatchList] Error searching batches:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [searchInput, effectiveLocationId, dateRange, savedDateRangeBeforeSearch]);

  // Clear search and restore previous date range
  const handleSearchClear = useCallback(() => {
    setSearchInput('');
    setActiveSearch('');
    setHasLoadedMore(false);

    // Restore previous date range
    const restoreRange = savedDateRangeBeforeSearch || '14d';
    setSavedDateRangeBeforeSearch(null);
    handleDateRangeChange(restoreRange);
  }, [savedDateRangeBeforeSearch, handleDateRangeChange]);

  // Calculate pagination state
  const hasMoreInCurrentRange = displayedBatches.length < totalCount;
  // Show expand option when all batches in current range are loaded AND there's a wider range to expand to
  // (We don't know if there are more batches in a wider date range, so always offer to expand)
  const canExpandDateRange = !hasMoreInCurrentRange && NEXT_EXPANSION[dateRange] !== null && !activeSearch;
  const remainingInCurrentRange = Math.max(0, totalCount - displayedBatches.length);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">Uploaded Contact Lists</h2>
          <select
            value={dateRange}
            onChange={(e) => {
              if (activeSearch) {
                // Clear search when user manually changes date range
                setSearchInput('');
                setActiveSearch('');
                setSavedDateRangeBeforeSearch(null);
              }
              handleDateRangeChange(e.target.value as DateRangeOption);
            }}
            className={`text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 ${activeSearch ? 'opacity-50' : ''}`}
            disabled={isLoadingMore || !!activeSearch}
          >
            {DATE_RANGE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Search bar */}
          <div className="relative flex items-center">
            <Search className="absolute left-2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchExecute();
                }
                if (e.key === 'Escape') {
                  if (activeSearch) {
                    handleSearchClear();
                  } else {
                    setSearchInput('');
                  }
                }
              }}
              placeholder="Search batch name..."
              className="text-sm border border-gray-300 rounded pl-8 pr-8 py-1 w-52 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoadingMore}
            />
            {(searchInput || activeSearch) && (
              <button
                onClick={handleSearchClear}
                className="absolute right-2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <button
          onClick={handleManualRefresh}
          className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center gap-2"
          title="Refresh batch list"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Active search indicator */}
      {activeSearch && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
          <Search className="h-4 w-4 flex-shrink-0" />
          <span>
            {totalCount === 0
              ? `No batches found matching "${activeSearch}"`
              : `Showing ${displayedBatches.length}${displayedBatches.length < totalCount ? ` of ${totalCount}` : ''} result${totalCount !== 1 ? 's' : ''} for "${activeSearch}" across all dates`
            }
          </span>
          <button
            onClick={handleSearchClear}
            className="ml-auto text-blue-500 hover:text-blue-700 text-sm font-medium"
          >
            Clear search
          </button>
        </div>
      )}

      {/* Custom date range picker */}
      {showCustomDatePicker && !activeSearch && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">From:</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">To:</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleCustomDateSearch}
            disabled={!customStartDate || isLoadingMore}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingMore ? 'Loading...' : 'Search'}
          </button>
        </div>
      )}

      {/* Empty-state for "no batches in this range / search" — totalCountAllTime > 0
          (otherwise the bare empty-state early return above would have fired).
          Search-active wording differs so it's clear the search filter, not the
          date range, is what's empty. */}
      {displayedBatches.length === 0 && !isLoadingMore && (() => {
        const nextRange = NEXT_EXPANSION[dateRange];
        const currentLabel = dateRange === 'custom'
          ? 'this date range'
          : DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label.toLowerCase() || 'this range';
        const nextLabel = nextRange
          ? DATE_RANGE_OPTIONS.find(o => o.value === nextRange)?.label
          : null;
        const allTimeText = totalCountAllTime !== null && totalCountAllTime > 0
          ? `${totalCountAllTime.toLocaleString()} batch${totalCountAllTime === 1 ? '' : 'es'}`
          : null;
        return (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            {activeSearch ? (
              <>
                <p className="text-gray-700 font-medium">No batches match "{activeSearch}".</p>
                {allTimeText !== null
                  ? null
                  : <p className="text-gray-500 mt-2 text-sm">Try a different search term.</p>}
              </>
            ) : (
              <>
                <p className="text-gray-700 font-medium">
                  No batches in {currentLabel}.
                </p>
                {allTimeText && (
                  <p className="text-gray-500 mt-2 text-sm">
                    You have {allTimeText} in your full history.
                  </p>
                )}
                {nextLabel && (
                  <button
                    onClick={handleExpandDateRange}
                    disabled={isLoadingMore}
                    className="mt-4 px-6 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Show {nextLabel}
                  </button>
                )}
              </>
            )}
          </div>
        );
      })()}

      {displayedBatches.map((batch) => {
        const messagingStatus = messagingStatuses.get(batch.id);
        
        // Parse services_enabled if it's a string
        const servicesEnabled = typeof batch.services_enabled === 'string'
          ? JSON.parse(batch.services_enabled)
          : batch.services_enabled;

        // Download gate: a batch with no paid scrub service has no legitimate
        // download purpose, and blocking it prevents extracting the silently
        // TCPA-scrubbed clean list for free. Internal locations are exempt.
        // (Backend enforces this too; this is UX only.)
        const downloadGated = !isInternal &&
          !(servicesEnabled?.landline === true || servicesEnabled?.tcpa === true);
        
        // Ready to Upload shows clean_count (only clean contacts are uploaded)
        const readyToUpload = batch.clean_count || 0;
        
        // Determine messaging button state
        const hasQueuedMessages = messagingStatus && messagingStatus.queued > 0;
        const hasPausedMessages = messagingStatus && messagingStatus.paused > 0;
        const hasProcessingMessages = messagingStatus && messagingStatus.processing > 0;
        // Use batch.messages_sent_at as fallback for immediate display before polling loads
        const messagingComplete = (messagingStatus &&
          (messagingStatus.completed + messagingStatus.failed) >= messagingStatus.total &&
          messagingStatus.total > 0) ||
          batch.messages_sent_at != null;
        const messagingInProgress = hasQueuedMessages || hasPausedMessages || hasProcessingMessages;
        
        // Check if messaging is scheduled but not yet started
        const now = new Date();
        const scheduledFor = batch.messaging_scheduled_for ? new Date(batch.messaging_scheduled_for) : null;
        const messagingHasStarted = scheduledFor && now >= scheduledFor;
        const isScheduledButNotStarted = scheduledFor && now < scheduledFor && messagingStatus && messagingStatus.total > 0;
        
        return (
        <div key={batch.id} className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                {getStatusIcon(batch.status)}
                <h3 className="text-lg font-semibold">{batch.batch_name}</h3>
                <span className="text-sm text-gray-500">
                  {getStatusText(batch.status, batch, messagingStatus)}
                </span>
                {batch.billing_paused_at && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Billing Paused
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(batch.created_at).toLocaleDateString()}
                </div>
                {batch.status === 'pending' && (
                  <div>
                    Validating: {(
                      (batch.needs_processing_count || 0) +
                      (batch.invalid_count || 0) +
                      (batch.duplicate_count || 0) +
                      (batch.known_landline_count || 0) +
                      (batch.known_tcpa_count || 0) +
                      (batch.already_processing_count || 0)
                    )} / {batch.total_rows_in_csv || 0} rows
                  </div>
                )}
                {batch.status === 'processing' && (
                  <div>
                    {(
                      (batch.clean_count || 0) +
                      (batch.landline_count || 0) +
                      (batch.tcpa_count || 0) +
                      (batch.error_count || 0)
                    )} / {batch.needs_processing_count} processed
                    {(batch.error_count || 0) > 0 && (
                      <span className="text-red-600 ml-2">({batch.error_count} errors)</span>
                    )}
                  </div>
                )}
                {/* Show message count if we have messaging data from either source */}
                {((messagingStatus && messagingStatus.total > 0) || (batch.tags_applied_count > 0 && batch.messages_sent_at)) && (
                  <div className="text-xs">
                    Messages: {Math.max(messagingStatus?.completed || 0, batch.tags_applied_count || 0)} / {messagingStatus?.total || batch.clean_count || 0}
                    {messagingStatus && messagingStatus.paused > 0 && <span className="text-orange-600 ml-1">(Paused)</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">{batch.original_filename}</span>
              {servicesEnabled && (
                <div className="flex items-center gap-2 text-xs">
                  <span 
                    className={`flex items-center gap-1 px-2 py-1 rounded ${
                      servicesEnabled.landline 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-500'
                    }`}
                    title={servicesEnabled.landline ? 'Landline Check Enabled' : 'Landline Check Disabled'}
                  >
                    <Shield className="w-3 h-3" />
                    Landline
                  </span>
                  <span 
                    className={`flex items-center gap-1 px-2 py-1 rounded ${
                      servicesEnabled.tcpa 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-500'
                    }`}
                    title={servicesEnabled.tcpa ? 'TCPA Check Enabled' : 'TCPA Check Disabled'}
                  >
                    <Scale className="w-3 h-3" />
                    TCPA
                  </span>
                </div>
              )}
              <div className="flex gap-2">
              {batch.status === 'completed' && (batch.processed_count || 0) > 0 && (
                <>
                  {!messagingComplete && !hasQueuedMessages && !hasPausedMessages && !isScheduledButNotStarted && !messagingHasStarted && (
                    <button
                      onClick={() => setSchedulingBatchId(batch.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded"
                      title="Schedule for Messaging"
                    >
                      <Play className="w-5 h-5" />
                    </button>
                  )}
                  {isScheduledButNotStarted && !messagingHasStarted && !messagingComplete && !hasQueuedMessages && !hasProcessingMessages && (
                    <button
                      onClick={() => setRescheduleMessagingBatchId(batch.id)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Reschedule Messaging"
                    >
                      <Calendar className="w-5 h-5" />
                    </button>
                  )}
                  {hasQueuedMessages && (
                    <button
                      onClick={() => handlePauseMessaging(batch.id)}
                      className="p-2 text-orange-600 hover:bg-orange-50 rounded"
                      title="Pause Messaging"
                    >
                      <Pause className="w-5 h-5" />
                    </button>
                  )}
                  {hasPausedMessages && (
                    <button
                      onClick={() => handleResumeMessaging(batch.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded"
                      title="Resume Messaging"
                    >
                      <PlayCircle className="w-5 h-5" />
                    </button>
                  )}
                  {(hasQueuedMessages || hasPausedMessages) && !messagingComplete && (
                    <button
                      onClick={() => handleCancelMessaging(batch.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Cancel Messaging"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                  {messagingComplete && (
                    <span className="px-3 py-2 text-xs bg-green-100 text-green-700 rounded font-medium">
                      Messaging Complete
                    </span>
                  )}
                  {(batch.status === 'completed' || 
                    batch.status === 'messaging_scheduled' || 
                    batch.status === 'messaging_in_progress' || 
                    batch.status === 'messaging_complete') && 
                   (batch.clean_count > 0 || (batch.total_contacts && batch.total_contacts > 0)) && (
                    downloadGated ? (
                      <button
                        disabled
                        className="p-2 text-gray-300 cursor-not-allowed rounded"
                        title="Download available on batches where a scrub service was selected"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => setExportingBatchId(batch.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Download Results"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                    )
                  )}
                  <button
                    onClick={() => handleReprocess(batch.id)}
                    className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                    title="ReRun List"
                  >
                    <RotateCcw className="w-5 h-5" />
                  </button>
                </>
              )}
                <button
                  onClick={() => handleDelete(batch.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {batch.status === 'upload_complete' && (
            <div className="mt-4 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <button
                  onClick={() => toggleExpanded(batch.id)}
                  className="w-full flex items-center justify-between text-sm font-medium text-yellow-900 mb-3 hover:text-yellow-700"
                >
                  <span>Upload Summary</span>
                  {expandedBatches.has(batch.id) ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                {expandedBatches.has(batch.id) && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-gray-600">Total in CSV</div>
                    <div className="font-semibold text-gray-900">{batch.total_rows_in_csv || 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Invalid</div>
                    <div className="font-semibold text-red-600">{batch.invalid_count || 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Duplicates</div>
                    <div className="font-semibold text-red-600">{batch.duplicate_count || 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Known Landlines</div>
                    <div className="font-semibold text-orange-600">{batch.known_landline_count || 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Known TCPA</div>
                    <div className="font-semibold text-orange-600">{batch.known_tcpa_count || 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Already Tagged</div>
                    <div className="font-semibold text-green-600">{batch.already_processing_count || 0}</div>
                  </div>
                  <div className="col-span-2 md:col-span-3 mt-3 pt-3 border-t border-yellow-200">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-gray-600">Parsed In</div>
                        <div className="text-lg font-semibold text-yellow-900">
                          {formatDuration(batch.created_at, batch.upload_completed_at)}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-600">Processed In</div>
                        <div className="text-lg font-semibold text-yellow-900">
                          {formatDuration(batch.processing_started_at, batch.processing_completed_at, batch.status === 'completed' && !batch.processing_completed_at)}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-600">Uploaded In</div>
                        <div className="text-lg font-semibold text-yellow-900">
                          {formatMessagingDuration(batch)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}
                <div className="mt-4 pt-4 border-t border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-600">Needs Processing</div>
                      <div className="text-2xl font-bold text-yellow-900">{batch.needs_processing_count || 0}</div>
                    </div>
                    {(batch.needs_processing_count || 0) > 0 && (
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Cost to Process</div>
                        <div className="text-2xl font-bold text-green-600">
                          ${(batch.estimated_revenue || 0).toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(batch.needs_processing_count || 0) > 0 && (
                // Only block for billing setup if there's a cost AND setup is incomplete
                // Free processing (estimated_revenue = 0) should always be allowed
                isSetupIncomplete && batch.estimated_revenue > 0 ? (
                  <div className="space-y-3">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CreditCard className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-amber-900">Billing Setup Required</p>
                          <p className="text-sm text-amber-700 mt-1">
                            Please add a payment method to process this batch.
                            {!setupStatus?.has_stripe_customer && ' A billing account will be created automatically.'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowAddCardModal(true)}
                      className="w-full px-6 py-3 rounded-lg font-semibold transition-colors bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                      <CreditCard className="w-5 h-5" />
                      Complete Billing Setup
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleContinueProcessing(batch.id)}
                    disabled={processingBatchIds.has(batch.id)}
                    className={`w-full px-6 py-3 rounded-lg font-semibold transition-colors text-white ${
                      processingBatchIds.has(batch.id)
                        ? 'bg-green-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {processingBatchIds.has(batch.id)
                      ? 'Processing...'
                      : (batch.estimated_revenue > 0 ? 'Continue and Pay' : 'Continue Processing')}
                  </button>
                )
              )}
            </div>
          )}

          {batch.status === 'pending' && (
            <div className="mt-4">
              <ProgressBar 
                processedCount={(
                  (batch.needs_processing_count || 0) +
                  (batch.invalid_count || 0) +
                  (batch.duplicate_count || 0) +
                  (batch.known_landline_count || 0) +
                  (batch.known_tcpa_count || 0) +
                  (batch.already_processing_count || 0)
                )}
                needsProcessingCount={batch.total_rows_in_csv || 0}
                label="Validating Rows..."
              />
            </div>
          )}

          {batch.status === 'processing' && (
            <ProgressBar
              processedCount={(
                (batch.clean_count || 0) +
                (batch.landline_count || 0) +
                (batch.tcpa_count || 0) +
                (batch.error_count || 0)
              )}
              needsProcessingCount={batch.needs_processing_count || 0}
            />
          )}

          {batch.status === 'completed' && (batch.processed_count || 0) === 0 && (batch.invalid_count || 0) > 0 && (
            <div className="mt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertCircle className="w-5 h-5" />
                  <div>
                    <div className="font-semibold">No Valid Contacts</div>
                    <div className="text-sm">All {batch.invalid_count} contacts in this batch were invalid. Contacts must have a valid phone number and at least a first name or last name.</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {batch.status === 'completed' && (batch.processed_count || 0) > 0 && !batch.messages_sent_at && (
            <div className="mt-4 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <button
                  onClick={() => toggleExpanded(batch.id)}
                  className="w-full flex items-center justify-between text-sm font-medium text-blue-900 mb-3 hover:text-blue-700"
                >
                  <span>Processing Results</span>
                  {expandedBatches.has(batch.id) ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                {expandedBatches.has(batch.id) && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <div className="text-gray-600">Total in CSV</div>
                        <div className="font-semibold text-gray-900">{batch.total_rows_in_csv || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Invalid</div>
                        <div className="font-semibold text-red-600">{batch.invalid_count || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Duplicates</div>
                        <div className="font-semibold text-red-600">{batch.duplicate_count || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Known Landlines</div>
                        <div className="font-semibold text-orange-600">{batch.known_landline_count || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Known TCPA</div>
                        <div className="font-semibold text-orange-600">{batch.known_tcpa_count || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Already Tagged</div>
                        <div className="font-semibold text-green-600">{batch.already_processing_count || 0}</div>
                      </div>
                    </div>
                    <div className="border-t border-blue-200 pt-4"></div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-gray-600">Processed</div>
                        <div className="text-lg font-semibold text-green-600">
                          {batch.processed_count || 0}
                        </div>
                      </div>
                    <div>
                      <div className="text-gray-600">Landlines</div>
                      <div className="text-lg font-semibold text-orange-600">
                        {batch.landline_count || 0}
                      </div>
                    </div>
                    <div></div>
                    <div>
                      <div className="text-gray-600">Errors</div>
                      <div className="text-lg font-semibold text-red-600">
                        {batch.error_count || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">TCPA Risk</div>
                      <div className="text-lg font-semibold text-red-600">
                        {batch.tcpa_count || 0}
                      </div>
                    </div>
                    <div></div>
                    <div className="col-span-3 mt-3 pt-3 border-t border-blue-300">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <div className="text-gray-600">Parsed In</div>
                          <div className="text-lg font-semibold text-blue-900">
                            {formatDuration(batch.created_at, batch.upload_completed_at)}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">Processed In</div>
                          <div className="text-lg font-semibold text-blue-900">
                            {formatDuration(batch.processing_started_at, batch.processing_completed_at, batch.status === 'completed' && !batch.processing_completed_at)}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">Uploaded In</div>
                          <div className="text-lg font-semibold text-blue-900">
                            {formatMessagingDuration(batch)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                )}
                {messagingInProgress && messagingStatus && !batch.messages_sent_at && (
                  <div className="mt-4">
                    <ProgressBar 
                      processedCount={messagingStatus.completed + messagingStatus.failed} 
                      needsProcessingCount={messagingStatus.total} 
                      label="Messaging Progress"
                    />
                  </div>
                )}
                <div className="border-t border-blue-200 pt-4 mt-4"></div>
                {!batch.messaging_scheduled_for && !messagingStatus?.processStarted ? (
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <div className="text-sm text-gray-600">Ready to Upload</div>
                      <div className="text-2xl font-bold text-blue-600">{readyToUpload}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Amount Paid</div>
                      <div className="text-2xl font-bold text-green-600">
                        ${(batch.actual_revenue || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">Clean Contacts</div>
                      <div className="text-2xl font-bold text-blue-600">{readyToUpload}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Remaining Messages</div>
                      <div className="text-2xl font-bold text-orange-600">
                        {messagingStatus?.remaining || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Messages Sent</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {/* Use MAX of real-time count and batch counter for stability */}
                        {Math.max(messagingStatus?.completed || 0, batch.tags_applied_count || 0)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Messages Errored</div>
                      <div className="text-2xl font-bold text-red-600">
                        {/* Use MAX of real-time count and batch counter for stability */}
                        {Math.max(messagingStatus?.failed || 0, batch.messages_errored_count || 0)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Amount Paid</div>
                      <div className="text-2xl font-bold text-green-600">
                        ${(batch.actual_revenue || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {batch.status === 'completed' && (batch.processed_count || 0) > 0 && batch.messages_sent_at && (
            <div className="mt-4 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <button
                  onClick={() => toggleExpanded(batch.id)}
                  className="w-full flex items-center justify-between text-sm font-medium text-green-900 mb-3 hover:text-green-700"
                >
                  <span>Messaging Results</span>
                  {expandedBatches.has(batch.id) ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                {expandedBatches.has(batch.id) && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <div className="text-gray-600">Total in CSV</div>
                        <div className="font-semibold text-gray-900">{batch.total_rows_in_csv || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Invalid</div>
                        <div className="font-semibold text-red-600">{batch.invalid_count || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Duplicates</div>
                        <div className="font-semibold text-red-600">{batch.duplicate_count || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Known Landlines</div>
                        <div className="font-semibold text-orange-600">{batch.known_landline_count || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Known TCPA</div>
                        <div className="font-semibold text-orange-600">{batch.known_tcpa_count || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Already Tagged</div>
                        <div className="font-semibold text-green-600">{batch.already_processing_count || 0}</div>
                      </div>
                    </div>
                    <div className="border-t border-green-200 pt-4"></div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-gray-600">Processed</div>
                        <div className="text-lg font-semibold text-green-600">
                          {batch.processed_count || 0}
                        </div>
                      </div>
                    <div>
                      <div className="text-gray-600">Landlines</div>
                      <div className="text-lg font-semibold text-orange-600">
                        {batch.landline_count || 0}
                      </div>
                    </div>
                    <div></div>
                    <div>
                      <div className="text-gray-600">Errors</div>
                      <div className="text-lg font-semibold text-red-600">
                        {batch.error_count || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">TCPA Risk</div>
                      <div className="text-lg font-semibold text-red-600">
                        {batch.tcpa_count || 0}
                      </div>
                    </div>
                    <div></div>
                    <div className="col-span-3 mt-3 pt-3 border-t border-green-300">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <div className="text-gray-600">Parsed In</div>
                          <div className="text-lg font-semibold text-green-900">
                            {formatDuration(batch.created_at, batch.upload_completed_at)}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">Processed In</div>
                          <div className="text-lg font-semibold text-green-900">
                            {formatDuration(batch.processing_started_at, batch.processing_completed_at, batch.status === 'completed' && !batch.processing_completed_at)}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600">Uploaded In</div>
                          <div className="text-lg font-semibold text-green-900">
                            {formatMessagingDuration(batch)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                )}
                <div className="border-t border-green-200 pt-4 mt-4"></div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Clean Contacts</div>
                    <div className="text-2xl font-bold text-green-600">{readyToUpload}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Remaining Messages</div>
                    <div className="text-2xl font-bold text-orange-600">
                      {messagingStatus?.remaining || 0}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Messages Sent</div>
                    <div className="text-2xl font-bold text-purple-600">
                      {/* Use MAX of real-time count and batch counter for stability */}
                      {Math.max(messagingStatus?.completed || 0, batch.tags_applied_count || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Messages Errored</div>
                    <div className="text-2xl font-bold text-red-600">
                      {/* Use MAX of real-time count and batch counter for stability */}
                      {Math.max(messagingStatus?.failed || 0, batch.messages_errored_count || 0)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Amount Paid</div>
                    <div className="text-2xl font-bold text-green-600">
                      ${(batch.actual_revenue || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        );
      })}

      {/* Pagination Info + Load More / Expand Date Range Button */}
      {displayedBatches.length > 0 && (
        <div className="flex flex-col items-center gap-2 pt-4">
          {/* Showing X of Y text */}
          <span className="text-sm text-gray-600">
            {displayedBatches.length === totalCount ? (
              // All loaded - show "Showing all X batches"
              dateRange === 'all' ? (
                `Showing all ${displayedBatches.length} batches`
              ) : dateRange === 'custom' ? (
                `Showing all ${displayedBatches.length} batches (Custom range)`
              ) : (
                `Showing all ${displayedBatches.length} batches (${DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label || ''})`
              )
            ) : (
              // More to load - show "Showing X of Y batches (date range)"
              dateRange === 'all' ? (
                `Showing ${displayedBatches.length} of ${totalCount} batches`
              ) : dateRange === 'custom' ? (
                `Showing ${displayedBatches.length} of ${totalCount} batches (Custom range)`
              ) : (
                `Showing ${displayedBatches.length} of ${totalCount} batches (${DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label || ''})`
              )
            )}
          </span>

          {/* Load More / Expand Button */}
          {(hasMoreInCurrentRange || canExpandDateRange) && (
          hasMoreInCurrentRange ? (
            <button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="px-6 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingMore ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Loading...
                </span>
              ) : (
                `Load ${Math.min(remainingInCurrentRange, BATCH_PAGE_SIZE)} More`
              )}
            </button>
          ) : canExpandDateRange && (
            <button
              onClick={handleExpandDateRange}
              disabled={isLoadingMore}
              className="px-6 py-2 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingMore ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Loading...
                </span>
              ) : (
                `Expand to ${DATE_RANGE_OPTIONS.find(o => o.value === NEXT_EXPANSION[dateRange])?.label || 'more'}`
              )}
            </button>
          )
          )}
        </div>
      )}

      {schedulingBatchId && (
        <ScheduleMessagingModal
          batchId={schedulingBatchId}
          batchName={displayedBatches.find(b => b.id === schedulingBatchId)?.batch_name || 'Unknown'}
          contactCount={displayedBatches.find(b => b.id === schedulingBatchId)?.clean_count || 0}
          onClose={() => setSchedulingBatchId(null)}
          onSchedule={(data) => handleScheduleMessaging(schedulingBatchId, data)}
        />
      )}

      {rerunningBatchId && (
        <RerunBatchModal
          batchId={rerunningBatchId}
          batchName={displayedBatches.find(b => b.id === rerunningBatchId)?.batch_name || 'Unknown'}
          cleanContactCount={displayedBatches.find(b => b.id === rerunningBatchId)?.clean_count || 0}
          onClose={() => setRerunningBatchId(null)}
          onRerun={(data) => handleRerun(rerunningBatchId, data)}
        />
      )}

      {rescheduleMessagingBatchId && (() => {
        const batch = displayedBatches.find(b => b.id === rescheduleMessagingBatchId);
        const messagingStatus = messagingStatuses.get(rescheduleMessagingBatchId);
        // Use intro_tag from messaging status if available, otherwise empty string
        const currentIntroTag = messagingStatus?.intro_tag || '';
        
        return (
          <ScheduleMessagingModal
            batchId={rescheduleMessagingBatchId}
            batchName={batch?.batch_name || 'Unknown'}
            contactCount={batch?.clean_count || 0}
            onClose={() => setRescheduleMessagingBatchId(null)}
            onSchedule={(data) => handleRescheduleMessaging(rescheduleMessagingBatchId, data)}
            isReschedule={true}
            currentScheduledFor={batch?.messaging_scheduled_for}
            currentIntroTag={currentIntroTag}
          />
        );
      })()}

      {exportingBatchId && (
        <ExportCSVModal
          batchId={exportingBatchId}
          batchName={displayedBatches.find(b => b.id === exportingBatchId)?.batch_name || 'Unknown'}
          totalContacts={displayedBatches.find(b => b.id === exportingBatchId)?.total_contacts || 0}
          cleanCount={displayedBatches.find(b => b.id === exportingBatchId)?.clean_count || 0}
          onClose={() => setExportingBatchId(null)}
        />
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
      />

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{confirmModal.title}</h3>
            <p className="text-sm text-gray-600 mb-4">{confirmModal.message}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className={`px-4 py-2 text-white rounded-md text-sm font-medium transition-colors ${
                  confirmModal.confirmColor === 'red'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {confirmModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Card Modal for wallet mode billing setup */}
      {showAddCardModal && effectiveLocationId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Complete Billing Setup</h2>
              <button
                onClick={() => setShowAddCardModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Add a payment method to enable list processing. Your card will be used for wallet top-ups and processing charges.
            </p>
            <AddCardForm
              locationId={effectiveLocationId}
              onSuccess={() => {
                setShowAddCardModal(false);
                // Refresh billing status to unblock Continue button
                onRefreshBillingStatus?.();
              }}
              onCancel={() => setShowAddCardModal(false)}
              setAsPrimaryTopup={true}
              setAsPrimarySubscription={true}
            />
          </div>
        </div>
      )}

      {/* Toast notification for new batch created */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={15000}
          onClose={() => setToast(null)}
          actions={toast.actions}
        />
      )}
    </div>
  );
}
